import { useEffect, useRef, useState } from "react";
import { formatRelativeTime } from "@/lib/caseStatus";

/**
 * "Updated 4 minutes ago" for a queue, kept honest as time passes.
 *
 * Two things have to happen and they are easy to separate by accident:
 *
 *  1. The stamp advances when a fetch lands — tracked off the falling edge of
 *     `loading` rather than on every render, so a background refetch that
 *     returns identical data still counts as fresh.
 *  2. **A timer forces a re-render every 30s.** Nothing else changes when a
 *     minute passes, so without this the label renders once and then lies for
 *     as long as the operator leaves the tab open.
 *
 * (2) looked like dead state in Dashboard.tsx — a `tick` counter incremented
 * on an interval and used only as a `key` — and was deleted as unused in the
 * Codex UI-refresh branch, which silently froze the label. Both halves live
 * here now so a second dashboard cannot inherit half of it.
 */
export function useUpdatedAgo(loading: boolean): string {
  const [lastUpdated, setLastUpdated] = useState(() => Date.now());
  const prevLoadingRef = useRef(loading);

  useEffect(() => {
    if (prevLoadingRef.current && !loading) {
      setLastUpdated(Date.now());
    }
    prevLoadingRef.current = loading;
  }, [loading]);

  // The re-render tick. The value is deliberately unused — incrementing it is
  // the entire point. Do not "clean this up".
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 30_000);
    return () => clearInterval(id);
  }, []);

  return formatRelativeTime(new Date(lastUpdated).toISOString());
}
